// src/components/DistrictTable.js
import React, { useState, useMemo } from 'react';
import './DistrictTable.css';
import { 
  extractSchoolNumber,
  getSchoolNumber,
  isDateOutdatedTmRk,
  isDateOutdatedcp,
  hasTimelinessProblems,
  hasErrorsInMenu,
  getSchoolCompliance,
  hasPublicationProblems,
  THRESHOLDS
} from '../utils/calculations';

const DistrictTable = ({ districtData, onDistrictClick, filters, allData }) => {
  const [sortConfig, setSortConfig] = useState({ column: 'district', direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProblem, setFilterProblem] = useState('all');
  const [tooltipData, setTooltipData] = useState({ visible: false, x: 0, y: 0, schools: [], problemType: '', district: '' });

  // Функция для получения номеров ОУ с определенной проблемой в районе
  // Используем ТЕ ЖЕ ДАННЫЕ, что и для districtData
  const getSchoolsWithProblem = (district, problemType) => {
    const districtSchools = allData.dates.filter(school => school.РайонНаименование === district);
    const schoolsWithProblem = [];
    
    districtSchools.forEach(school => {
      let hasProblem = false;
      
      switch (problemType) {
        case 'tm':
          hasProblem = isDateOutdatedTmRk(school.ДатаТМ);
          break;
        case 'kp':
          hasProblem = isDateOutdatedcp(school.ДатаКП);
          break;
        case 'rk':
          hasProblem = isDateOutdatedTmRk(school.ДатаРК);
          break;
        case 'errors':
          hasProblem = hasErrorsInMenu(school, allData.errors);
          break;
        case 'compliance':
          const avgCompliance = getSchoolCompliance(school, allData.compliance);
          hasProblem = avgCompliance < THRESHOLDS.COMPLIANCE_CRITICAL;
          break;
        case 'publication':
          hasProblem = hasPublicationProblems(school, allData, filters.month, filters.year);
          break;
        case 'timeliness':
          hasProblem = hasTimelinessProblems(school, allData.timeliness, THRESHOLDS.TIMELINESS_GOOD);
          break;
        default:
          hasProblem = false;
      }
      
      if (hasProblem) {
        const schoolNumber = extractSchoolNumber(school.Наименование);
        if (schoolNumber) {
          schoolsWithProblem.push(schoolNumber);
        } else {
          const shortName = school.Наименование
            .replace('ГБОУ ', '')
            .replace(' СОШ', '')
            .replace(' СПб', '')
            .substring(0, 25);
          schoolsWithProblem.push(shortName);
        }
      }
    });
    
    return schoolsWithProblem.sort((a, b) => {
      if (typeof a === 'number' && typeof b === 'number') return a - b;
      return String(a).localeCompare(String(b));
    });
  };

  // Обработчики для тултипа
  const handleMouseEnter = (e, district, problemType, count) => {
    if (count === 0) return;
    
    const schools = getSchoolsWithProblem(district, problemType);
    
    if (schools.length === 0) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipData({
      visible: true,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
      schools: schools,
      problemType: problemType,
      district: district
    });
  };

  const handleMouseLeave = () => {
    setTooltipData({ visible: false, x: 0, y: 0, schools: [], problemType: '', district: '' });
  };

  // Получение названия проблемы для тултипа
  const getProblemTitle = (problemType) => {
    const titles = {
      'tm': 'Типовое меню',
      'kp': 'Календарь питания',
      'rk': 'Родительский контроль',
      'errors': 'Ошибки в меню',
      'compliance': 'Низкое соответствие',
      'publication': 'Проблемы публикации',
      'timeliness': 'Низкая своевременность'
    };
    return titles[problemType] || 'Проблема';
  };

  // Фильтрация и сортировка данных
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...districtData];
    
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.district.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterProblem === 'hasProblems') {
      filtered = filtered.filter(item => item.problemSchools > 0);
    } else if (filterProblem === 'noProblems') {
      filtered = filtered.filter(item => item.problemSchools === 0);
    }
    
    filtered.sort((a, b) => {
      let valueA, valueB;
      
      switch (sortConfig.column) {
        case 'district':
          valueA = a.district;
          valueB = b.district;
          break;
        case 'totalSchools':
          valueA = a.totalSchools;
          valueB = b.totalSchools;
          break;
        case 'problemSchools':
          valueA = a.problemSchools;
          valueB = b.problemSchools;
          break;
        case 'problemPercentage':
          valueA = a.problemPercentage;
          valueB = b.problemPercentage;
          break;
        case 'tm':
          valueA = a.problemsByType['Устаревшее ТМ'] || 0;
          valueB = b.problemsByType['Устаревшее ТМ'] || 0;
          break;
        case 'kp':
          valueA = a.problemsByType['Устаревшее КП'] || 0;
          valueB = b.problemsByType['Устаревшее КП'] || 0;
          break;
        case 'rk':
          valueA = a.problemsByType['Устаревшее РК'] || 0;
          valueB = b.problemsByType['Устаревшее РК'] || 0;
          break;
        case 'errors':
          valueA = a.problemsByType['Ошибки в меню'] || 0;
          valueB = b.problemsByType['Ошибки в меню'] || 0;
          break;
        case 'compliance':
          valueA = a.problemsByType['Низкое соответствие'] || 0;
          valueB = b.problemsByType['Низкое соответствие'] || 0;
          break;
        case 'publication':
          valueA = a.problemsByType['Проблемы публикации'] || 0;
          valueB = b.problemsByType['Проблемы публикации'] || 0;
          break;
        case 'timeliness':
          valueA = a.problemsByType['Низкая своевременность'] || 0;
          valueB = b.problemsByType['Низкая своевременность'] || 0;
          break;
        default:
          valueA = a.district;
          valueB = b.district;
      }
      
      if (typeof valueA === 'string') {
        return sortConfig.direction === 'asc' 
          ? valueA.localeCompare(valueB, 'ru')
          : valueB.localeCompare(valueA, 'ru');
      }
      
      return sortConfig.direction === 'asc' ? valueA - valueB : valueB - valueA;
    });
    
    return filtered;
  }, [districtData, sortConfig, searchTerm, filterProblem]);

  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortSymbol = (column) => {
    if (sortConfig.column === column) {
      return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    }
    return ' ↕';
  };

  const getProblemLevel = (percentage) => {
    if (percentage > 50) return 'critical';
    if (percentage > 25) return 'warning';
    return 'normal';
  };

  const stats = {
    totalDistricts: filteredAndSortedData.length,
    totalSchools: filteredAndSortedData.reduce((sum, d) => sum + d.totalSchools, 0),
    totalProblemSchools: filteredAndSortedData.reduce((sum, d) => sum + d.problemSchools, 0),
    avgProblemPercentage: filteredAndSortedData.length > 0 
      ? (filteredAndSortedData.reduce((sum, d) => sum + d.problemPercentage, 0) / filteredAndSortedData.length).toFixed(1)
      : 0
  };

  return (
    <div className="district-table-container">
      <div className="table-control-panel">
        <div className="search-box">
          <input
            type="text"
            placeholder="Поиск района..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filterProblem === 'all' ? 'active' : ''}`}
            onClick={() => setFilterProblem('all')}
          >
            Все районы
          </button>
          <button 
            className={`filter-btn has-problems ${filterProblem === 'hasProblems' ? 'active' : ''}`}
            onClick={() => setFilterProblem('hasProblems')}
          >
            С проблемами
          </button>
          <button 
            className={`filter-btn no-problems ${filterProblem === 'noProblems' ? 'active' : ''}`}
            onClick={() => setFilterProblem('noProblems')}
          >
            Без проблем
          </button>
        </div>
        
        <div className="table-stats">
          <span className="stat-badge">Районов: {stats.totalDistricts}</span>
          <span className="stat-badge">ОУ: {stats.totalSchools}</span>
          <span className="stat-badge warning">Проблемных ОУ: {stats.totalProblemSchools}</span>
          <span className="stat-badge">Средний %: {stats.avgProblemPercentage}%</span>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="district-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('district')} className="sortable">
                Район {getSortSymbol('district')}
              </th>
              <th onClick={() => handleSort('totalSchools')} className="sortable center">
                ОУ {getSortSymbol('totalSchools')}
              </th>
              <th onClick={() => handleSort('problemSchools')} className="sortable center">
                Проблемных ОУ {getSortSymbol('problemSchools')}
              </th>
              <th onClick={() => handleSort('problemPercentage')} className="sortable center">
                % проблем {getSortSymbol('problemPercentage')}
              </th>
              <th onClick={() => handleSort('tm')} className="sortable center" title="Типовое меню">
                ТМ {getSortSymbol('tm')}
              </th>
              <th onClick={() => handleSort('kp')} className="sortable center" title="Календарь питания">
                КП {getSortSymbol('kp')}
              </th>
              <th onClick={() => handleSort('rk')} className="sortable center" title="Родительский контроль">
                РК {getSortSymbol('rk')}
              </th>
              <th onClick={() => handleSort('errors')} className="sortable center" title="Ошибки в меню">
                Ошибки {getSortSymbol('errors')}
              </th>
              <th onClick={() => handleSort('compliance')} className="sortable center" title="Низкое соответствие">
                Соотв. {getSortSymbol('compliance')}
              </th>
              <th onClick={() => handleSort('publication')} className="sortable center" title="Проблемы публикации">
                Публик. {getSortSymbol('publication')}
              </th>
              <th onClick={() => handleSort('timeliness')} className="sortable center" title="Низкая своевременность">
                Своевр. {getSortSymbol('timeliness')}
              </th>
              <th className="center">Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.map((district) => {
              const problemLevel = getProblemLevel(district.problemPercentage);
              
              return (
                <tr 
                  key={district.district} 
                  className={`district-row ${problemLevel}`}
                  onClick={() => onDistrictClick(district.district)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="district-name">
                    <strong>{district.district}</strong>
                  </td>
                  <td className="center">{district.totalSchools}</td>
                  <td className={`center ${district.problemSchools > 0 ? 'problem-count' : 'success-count'}`}>
                    {district.problemSchools}
                  </td>
                  <td className="center">
                    <div className="percentage-cell">
                      <span className={`percentage-value ${problemLevel}`}>
                        {district.problemPercentage}%
                      </span>
                      <div className="percentage-bar-container">
                        <div 
                          className={`percentage-bar ${problemLevel}`}
                          style={{ width: `${district.problemPercentage}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td 
                    className="center tooltip-cell"
                    onMouseEnter={(e) => handleMouseEnter(e, district.district, 'tm', district.problemsByType['Устаревшее ТМ'])}
                    onMouseLeave={handleMouseLeave}
                  >
                    {district.problemsByType['Устаревшее ТМ'] > 0 ? (
                      <span className="problem-badge tm">{district.problemsByType['Устаревшее ТМ']}</span>
                    ) : (
                      <span className="success-badge">✓</span>
                    )}
                  </td>
                  <td 
                    className="center tooltip-cell"
                    onMouseEnter={(e) => handleMouseEnter(e, district.district, 'kp', district.problemsByType['Устаревшее КП'])}
                    onMouseLeave={handleMouseLeave}
                  >
                    {district.problemsByType['Устаревшее КП'] > 0 ? (
                      <span className="problem-badge kp">{district.problemsByType['Устаревшее КП']}</span>
                    ) : (
                      <span className="success-badge">✓</span>
                    )}
                  </td>
                  <td 
                    className="center tooltip-cell"
                    onMouseEnter={(e) => handleMouseEnter(e, district.district, 'rk', district.problemsByType['Устаревшее РК'])}
                    onMouseLeave={handleMouseLeave}
                  >
                    {district.problemsByType['Устаревшее РК'] > 0 ? (
                      <span className="problem-badge rk">{district.problemsByType['Устаревшее РК']}</span>
                    ) : (
                      <span className="success-badge">✓</span>
                    )}
                  </td>
                  <td 
                    className="center tooltip-cell"
                    onMouseEnter={(e) => handleMouseEnter(e, district.district, 'errors', district.problemsByType['Ошибки в меню'])}
                    onMouseLeave={handleMouseLeave}
                  >
                    {district.problemsByType['Ошибки в меню'] > 0 ? (
                      <span className="problem-badge errors">{district.problemsByType['Ошибки в меню']}</span>
                    ) : (
                      <span className="success-badge">✓</span>
                    )}
                  </td>
                  <td 
                    className="center tooltip-cell"
                    onMouseEnter={(e) => handleMouseEnter(e, district.district, 'compliance', district.problemsByType['Низкое соответствие'])}
                    onMouseLeave={handleMouseLeave}
                  >
                    {district.problemsByType['Низкое соответствие'] > 0 ? (
                      <span className="problem-badge compliance">{district.problemsByType['Низкое соответствие']}</span>
                    ) : (
                      <span className="success-badge">✓</span>
                    )}
                  </td>
                  <td 
                    className="center tooltip-cell"
                    onMouseEnter={(e) => handleMouseEnter(e, district.district, 'publication', district.problemsByType['Проблемы публикации'])}
                    onMouseLeave={handleMouseLeave}
                  >
                    {district.problemsByType['Проблемы публикации'] > 0 ? (
                      <span className="problem-badge publication">{district.problemsByType['Проблемы публикации']}</span>
                    ) : (
                      <span className="success-badge">✓</span>
                    )}
                  </td>
                  <td 
                    className="center tooltip-cell"
                    onMouseEnter={(e) => handleMouseEnter(e, district.district, 'timeliness', district.problemsByType['Низкая своевременность'])}
                    onMouseLeave={handleMouseLeave}
                  >
                    {district.problemsByType['Низкая своевременность'] > 0 ? (
                      <span className="problem-badge timeliness">{district.problemsByType['Низкая своевременность']}</span>
                    ) : (
                      <span className="success-badge">✓</span>
                    )}
                  </td>
                  <td className="center">
                    <button 
                      className="view-district-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDistrictClick(district.district);
                      }}
                      title="Перейти к району"
                    >
                      📊
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Тултип с номерами ОУ */}
      {tooltipData.visible && tooltipData.schools.length > 0 && (
        <div 
          className="schools-tooltip"
          style={{
            position: 'fixed',
            left: tooltipData.x,
            top: tooltipData.y,
            transform: 'translateX(-50%) translateY(-100%)'
          }}
        >
          <div className="tooltip-header">
            {getProblemTitle(tooltipData.problemType)} - {tooltipData.district}
          </div>
          <div className="tooltip-count">
            {tooltipData.schools.length} образовательных учреждений:
          </div>
          <div className="tooltip-schools">
            {tooltipData.schools.slice(0, 30).map((school, idx) => (
              <span key={idx} className="tooltip-school">
                {typeof school === 'number' ? `№${school}` : school}
              </span>
            ))}
            {tooltipData.schools.length > 30 && (
              <span className="tooltip-more">
                + еще {tooltipData.schools.length - 30}
              </span>
            )}
          </div>
        </div>
      )}
      
      {filteredAndSortedData.length === 0 && (
        <div className="no-results">
          <p>Районы не найдены</p>
          <button onClick={() => { setSearchTerm(''); setFilterProblem('all'); }}>
            Сбросить фильтры
          </button>
        </div>
      )}
    </div>
  );
};

export default DistrictTable;